import React, { useState, createContext, useContext, useEffect, useMemo } from 'react';
import { getEntityLink, useRedirect } from '../../Services/helpers';
import Callback from '../../Services/Callback';
import { ulid } from 'ulid';
import API from '../../Services/API';
import { useSession } from '../Session';
import * as serviceWorkerRegistration from '../../serviceWorkerRegistration';

const MobileContext = createContext();

export const useMobileContext = () => {
    return useContext(MobileContext);
};

const receiveMessageCallback = new Callback();

export const Provider = ({ children }) => {
    const {go, Redirect} = useRedirect();
    const [deviceToken, setDeviceToken] = useState(null);
    const [notificationsEnabled, setNotificationsEnabled] = useState(null);
    const isMobile = !!window.ReactNativeWebView;
    const {notificationBadge, setDeviceToken: setSessionDeviceToken, isAdmin} = useSession();
    const [supportsServiceWorker, setSupportsServiceWorker] = useState(false);
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [serviceWorkerUpdated, setServiceWorkerUpdated] = useState(false);

    const [appVersion, isIos, isAndroid] = useMemo(() => {
        const isIos = /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream;
        const isAndroid = /Android/.test(window.navigator.userAgent);

        if (!isMobile) {
            return [null, isIos, isAndroid];
        }

        return [
            window.navigator.userAgent.match(/SoapyApp\/([0-9.]+)/)?.[1] ?? null,
            isIos,
            isAndroid
        ];
    }, [isMobile]);

    useEffect(() => {
        register();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setNotificationsBadge(notificationBadge);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notificationBadge]);

    const register = () => {
        registerWithNative();
        registerServiceWorker();
    }

    const registerWithNative = () => {
        if (!isMobile) {
            return;
        }

        // this.request('resetNotifications')
        //     .then(perms => console.log('Reset permissions', perms));

        window.receiveMessageFromMobile = (json) => {
            const data = JSON.parse(json);
            console.log('Message received', data);
            receiveMessageCallback.invoke(data)
        };

        registerListeners();
        request('ready').then(res => {
            console.log('Ready acknowledged', res);
            getNotificationStatus();
        });
    }

    const registerServiceWorker = () => {
        if (!isMobile || !window?.navigator?.serviceWorker) {
            return;
        }

        if (!isAdmin()) {
            return;
        }

        setSupportsServiceWorker(true);

        serviceWorkerRegistration.register({
            onUpdate: () => setUpdateAvailable(true),
            onSuccess: () => setServiceWorkerUpdated(true),
        });
    }

    const registerListeners = () => {
        addListener((msg) => {
            if (!msg || !msg?.event) {
                return;
            }

            switch (msg.event) {
                case 'redirect':
                    handleRedirect(msg.url);
                    // Tell the mobile app we've handled the URL so it stops trying to load it
                    request('urlHandled', {url: msg.url});
                    break;

                case 'notificationsRegistered':
                    console.log('Notifications registered', msg.data)
                    setDeviceToken(msg.data.token);
                    setSessionDeviceToken(msg.data.token);

                    if (API.isAdminSwitchedBusiness() === false) {
                        API.post('/notifications/registerDevice', {
                            deviceToken: msg.data.token,
                            platform: msg.data.os ?? window.navigator.platform
                        });
                    }
                    break;

                case 'notificationSelected':
                    if (msg?.data?.entity?.id) {
                        go(getEntityLink(msg.data.entity.model, msg.data.entity.id));
                    }
                    break;

                default:
                    console.warn('Unhandled event', msg);
                    break;
            }
        })
    }

    const postMessage = (data) => {
        if (!isMobile) {
            return;
        }

        console.log('Posting message', data);
        window.ReactNativeWebView.postMessage(JSON.stringify(data));
    }

    const request = (request, data) => {
        return new Promise((resolve, reject) => {
            const requestId = ulid();

            let _removeListener;
            const listener = (data) => {
                if (data.requestId !== requestId) {
                    return;
                }

                resolve(data.result);
                _removeListener();
            }

            _removeListener = addListener(listener);

            const payload = Object.assign(
                {
                    requestId,
                    request,
                },
                data ?? {}
            );

            postMessage(payload);
        });
    }

    const addListener = (listener) => {
        return receiveMessageCallback.listen(listener);
    }

    const removeListener = (listener) => {
        return receiveMessageCallback.remove(listener);
    }

    const handleRedirect = (url) => {
        if (!url) {
            return;
        }

        if (url.indexOf('gspy.uk') > -1) {
            const code = url.replace('https://gspy.uk/', '');
            go(`/redirect-short-link/${code}`);
            return;
        }

        const cleanedUrl = url.replace(`${window.location.protocol}//${window.location.host}`, '').replace('https://app.getsoapy.com', '');
        go(cleanedUrl);
    }

    const setNotificationsBadge = (number) => {
        // Typo in the native code, remove the double t once the fix is out :facepalm:
        return request('settNotificationBadge', { number });
    }

    const getNotificationStatus = () => {
        request('notificationsStatus').then(res => {
            console.log('Notification status', res);
            setNotificationsEnabled(res.alert)

            // If notifications are enabled, request them anyway
            // This won't show anything to the user but will ensure the
            // Device ID is picked up, in case it was missed before
            if (res.alert) {
                requestNotifications();
            }
        });
    }

    const requestNotifications = () => {
        return request('requestNotifications').then(res => setNotificationsEnabled(res.alert));
    }

    const providerValues = {
        deviceToken,
        addListener,
        removeListener,
        isMobile,
        isIos,
        isAndroid,
        request,
        register,
        registerListeners,
        postMessage,
        setNotificationsBadge,
        notificationsEnabled,
        requestNotifications,
        appVersion,
        supportsServiceWorker,
        updateAvailable,
        serviceWorkerUpdated,
    };

    return (
        <MobileContext.Provider value={providerValues}>
            {children}
            <Redirect />
        </MobileContext.Provider>
    );
};
